import { useAuth } from "@clerk/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useState, type ReactElement } from "react";
import type { WorkspaceSummaryResponse } from "../../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import {
  isOnRd,
  ownChatsFor,
  railChatList,
  railRdChevron,
  railRdMark,
  railRdOpen,
  railSummaryParam,
} from "@/lib/rd-chats";
import { activeChat } from "@/lib/rd-page-tabs";
import type { RailRd } from "@/lib/rail-sections";
import { RailRdRow, type RenderRailLink } from "./always-rail-parts";
import { RailRdChats, type RailRdChatsState } from "./rail-rd-chats";

// The rail's chats under each RD (NO-754): which RD rows are open, and each
// open RD's workspace summary, read on the RD page's own query key so the RD
// you are on shares its cache entry. The choices live in memory for as long
// as the shell is mounted, so an RD opened with its chevron stays open while
// you navigate. Shell chrome, so only always-rail.tsx imports it.

export interface RailChatChoices {
  /** A chevron click per RD id; absent means "open while you are on it". */
  open: Readonly<Record<string, boolean>>;
  setOpen: (rdId: string, open: boolean) => void;
  /** RD ids whose "Show N more" was clicked. */
  showAll: ReadonlySet<string>;
  showMore: (rdId: string) => void;
}

export function useRailChatChoices(): RailChatChoices {
  const [open, setOpenState] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<ReadonlySet<string>>(() => new Set());
  const setOpen = useCallback((rdId: string, value: boolean) => {
    setOpenState((previous) => ({ ...previous, [rdId]: value }));
  }, []);
  const showMore = useCallback((rdId: string) => {
    setShowAll((previous) => new Set(previous).add(rdId));
  }, []);

  return { open, setOpen, showAll, showMore };
}

type SummaryQuery = UseQueryResult<{ data: WorkspaceSummaryResponse }>;

function useRailRdSummary(param: string, enabled: boolean): SummaryQuery {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["workspace-summary", param],
    queryFn: () =>
      authenticatedApi<{ data: WorkspaceSummaryResponse }>(
        getToken,
        `/api/rds/${param}/chats`
      ),
    enabled,
  });
}

function chatsState(
  query: SummaryQuery,
  rd: RailRd,
  showAll: boolean
): RailRdChatsState {
  const own = ownChatsFor(query.data?.data, rd);
  if (own) return { status: "ready", ...railChatList(own, showAll) };

  return query.isError ? { status: "error" } : { status: "loading" };
}

/** The RD row that hosts this RD's chats: its chevron, and the chats once open. */
export function RailRdChatsEntry({
  rd,
  current,
  pathname,
  collapsed,
  choices,
  renderLink,
}: {
  rd: RailRd;
  current: boolean;
  pathname: string;
  collapsed: boolean;
  choices: RailChatChoices;
  renderLink: RenderRailLink;
}): ReactElement {
  const onRd = isOnRd(rd, pathname);
  const choice = choices.open[rd.id];
  const open = railRdOpen(onRd, choice);
  const query = useRailRdSummary(railSummaryParam(rd, pathname), open);
  const chatCount = ownChatsFor(query.data?.data, rd)?.length ?? null;
  const chat = activeChat(pathname);
  const state = chatsState(query, rd, choices.showAll.has(rd.id));
  const mark = railRdMark({
    current,
    open,
    newChat: onRd && chat.newChat,
    chatId: onRd ? chat.chatId : null,
    listedChatIds: state.status === "ready" ? state.chats.map((c) => c.chatId) : [],
  });

  return (
    <div>
      <RailRdRow
        rd={rd}
        current={mark === "current"}
        parent={mark === "parent"}
        collapsed={collapsed}
        renderLink={renderLink}
        disclosure={
          railRdChevron({ open, chosen: choice !== undefined, chatCount })
            ? { open, onToggle: () => choices.setOpen(rd.id, !open) }
            : undefined
        }
      />
      {open ? (
        <RailRdChats
          rd={rd}
          state={state}
          currentChatId={onRd ? chat.chatId : null}
          newChatCurrent={onRd && chat.newChat}
          collapsed={collapsed}
          onShowMore={() => choices.showMore(rd.id)}
          renderLink={renderLink}
        />
      ) : null}
    </div>
  );
}
